<script setup lang="ts">
import { onMounted, ref, type ComponentInternalInstance } from 'vue';
import CredentialCard from './CredentialCard.vue';
import { sendFormData } from '../js-utils/_shared/fetch';
import { type Afiliado } from '../types';

// declare required types / interfaces
type Response = {
    afiliado: Afiliado;
    grupoFamiliar: Array<Afiliado>;
};

// declare the component props
const props = defineProps({
    title: String,
    requestToken: String,
    culture: String,
    apiUrl: String,
    apiPdfUrl: String,
    username: String,
    headlinePrefix: String,
    headline: String,
    downloadText: String,
    grupoFamiliarTitle: String,
});

// declare the reactive properties
const fetching = ref(false);
const errorMessage = ref<string | null>(null);
const response = ref<Response | null>(null);

// implement tasks on coponent mount
onMounted(async (callback: () => void, target?: ComponentInternalInstance | null) => {
    fetching.value = true;
    try {
        const json = await sendFormData(props.apiUrl!, {
            username: props.username,
            encodeResult: false,
        });
        const grupoFamiliar = json.value!.grupoFamiliar as Array<Afiliado>;
        const afiliado = grupoFamiliar[0]!;
        response.value = { afiliado, grupoFamiliar: grupoFamiliar!.slice(1) };
    } catch {
        errorMessage.value = 'No pudo obtenerse la información';
    } finally {
        fetching.value = false;
    }
});
</script>

<template>
    <div class="panel panel-credencial">
        <section class="container credencial-digital form-card">
            <div class="row">
                <section class="col-9 title-col">
                    <h1>{{ props.title }}</h1>
                    <div v-if="!response">
                        <form>
                            <input type="hidden" name="__RequestVerificationToken" :value="props.requestToken" />
                            <input type="hidden" name="username" :value="props.username" />
                        </form>
                        <div v-if="fetching" class="processing-box">Obteniendo la información...</div>
                        <div v-if="errorMessage" class="failure-box">
                            {{ errorMessage }}
                        </div>
                    </div>
                    <div v-else>
                        <h2>
                            {{ props.headlinePrefix }}
                            <span class="name-user">{{ response.afiliado.nombre }}</span
                            >{{ props.headline }}
                        </h2>
                    </div>
                </section>
            </div>
        </section>

        <section v-if="response?.afiliado" class="container container-card-credencial">
            <div class="col-12 user-group">
                <div class="row">
                    <CredentialCard
                        :afiliado="response.afiliado"
                        :apiPdfUrl="props.apiPdfUrl!.toString()"
                        :requestToken="props.requestToken!.toString()"
                        :downloadText="props.downloadText!.toString()"></CredentialCard>
                </div>
            </div>
        </section>
        <section v-if="response?.grupoFamiliar && response.grupoFamiliar.length > 0" class="container family-container">
            <div class="col-12 family-group">
                <div class="row">
                    <h3 class="title-credencial">{{ props.grupoFamiliarTitle }}</h3>
                </div>
                <div class="row">
                    <div v-for="afiliado in response.grupoFamiliar!" class="family-card-container">
                        <p class="family-name">{{ afiliado.nombreApellido }}</p>
                        <CredentialCard
                            :afiliado="afiliado"
                            :apiPdfUrl="props.apiPdfUrl!.toString()"
                            :requestToken="props.requestToken!.toString()"
                            :downloadText="props.downloadText!.toString()"></CredentialCard>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>
